import Backbone from "backbone";
import api from "core/api/api";
import { loadDGsForAgents, getAgentForDeploymentGroup } from "app/components/flow/designer/agents-loader";
import { DB_PROVIDERS } from "./configs/db-providers";
import * as PROVIDERS from "./configs/providers";
import { getParams } from "../../../../../../../src/modules/wizards/services";
import { isAutomatedWizard } from "./app-services";
import { shouldFetchConnectionURL } from "../../../../../../../src/modules/wizards/services/wizards-methods-helper";

// ─── Table Wrapper → API dispatch ──────────────────────────────────
export const TABLE_WRAPPER_API = {
    Schema: (wiz, params, agent, metaObjectFQN, adapter) =>
        api.WizardHelper_GetSchemas(wiz, params, agent, metaObjectFQN, adapter),
    Database: (wiz, params, agent, metaObjectFQN, adapter) =>
        api.WizardHelper_GetDBs(wiz, params, agent, metaObjectFQN, adapter),
    Object: (wiz, params, agent, metaObjectFQN, adapter) =>
        api.WizardHelper_GetObjects(wiz, params, agent, metaObjectFQN, adapter),
    Dataset: (wiz, params, agent, metaObjectFQN, adapter) =>
        api.WizardHelper_GetDatasets(wiz, params, agent, metaObjectFQN, adapter),
    Table: (wiz, params, agent, metaObjectFQN, adapter) =>
        api.WizardHelper_GetObjects(wiz, params, agent, metaObjectFQN, adapter)
};

// ─── Helpers ───────────────────────────────────────────────────────
function getProviderConfig(type) {
    return PROVIDERS[type];
}

function resolveWizardType(config, type) {
    return config?.wizardType || type.toUpperCase();
}

export const ConnectionParams = Backbone.Model.extend({
    defaults: {
        host: "",
        port: "",
        username: "",
        password: "",
        encrypted: false,
        consumerKey: "",
        consumerSecret: "",
        securityToken: "",
        authToken: "",
        mode: null,
        customObjects: false,
        dbName: "",
        sid: "",
        agentName: null,
        agentDGName: null,
        type: null,
        validateSourceOnAgent: null,
        connectionUrl: null,
        isOauth: true,
        clientId: "",
        clientSecret: "",
        refreshToken: "",
        privateAppToken: "",
        clientCustomerId: "",
        managerId: "",
        accessToken: "",
        refreshToken: "",
        developerToken: "",
        isPDB: false,
        containerName: "",
        useConnectionProfile: false,
        connectionProfileName: "",
        primaryConnectionProfileName: "",
        objectID: null
    },

    isAppAdapter() {
        return DB_PROVIDERS[this.get("type")]?.isAppAdapter === true;
    },

    getJDBCURL() {
        const type = this.get("type");
        const canFetchConnectionUrl = shouldFetchConnectionURL(this.get("useConnectionProfile"), type);
        if (!canFetchConnectionUrl) return null;

        if (this.get("connectionUrl")) return this.get("connectionUrl");
        const config = getProviderConfig(type);
        if (config?.getJDBCURL) return config.getJDBCURL(this);
        return this.get("connectionUrl");
    },
    getJDBCDownstreamURL() {
        let type = this.get("type");
        if (type === DB_PROVIDERS.OJET.id) {
            return (
                "jdbc:oracle:thin:@//" +
                this.get("primaryDatabaseHostname") +
                ":" +
                this.get("primaryDatabasePort") +
                "/" +
                this.get("serviceName")
            );
        }
    },
    isDownstreamInfoFilled() {
        const connectionInfoFilled =
            (this.get("supportDownStreamCapture") &&
                this.get("primaryDatabaseUsername") &&
                this.get("primaryDatabasePassword") &&
                this.get("primaryDatabaseHostname") &&
                this.get("primaryDatabaseServiceName") &&
                this.get("primaryDatabasePort")) ||
            this.get("primaryConnectionProfileName");
        return connectionInfoFilled || false;
    },

    checkResponse(payload) {
        try {
            if (payload) {
                if (typeof payload === "string" && payload === "Completed.") {
                    return;
                }
                if (typeof payload === "string") {
                    payload = JSON.parse(payload);
                }
                if (payload.result !== true) {
                    throw new Error(payload.message);
                }
            }
        } catch (err) {
            console.log(err);
        }
    },
    getTables() {},
    async checkConnection(isCDC, checkPrimaryDatabase = false) {
        let type = this.get("type").toLowerCase();
        let username = this.get("username");
        let password = this.get("password");
        const wizardURL = this.getJDBCURL();
        const useConnectionProfile = this.get("useConnectionProfile");
        const connectionProfileName = this.get("connectionProfileName");
        let agentName = null;
        let agentDGName = null;
        const selectedAgentValue = this.get("validateSourceOnAgent");
        if (selectedAgentValue === true) {
            const agentDeploymentGroups = loadDGsForAgents();
            let agentDeploymentGroup = agentDeploymentGroups[0].get("id");
            agentDGName = agentDeploymentGroup;
            agentName = getAgentForDeploymentGroup(agentDeploymentGroup);
        } else if (typeof selectedAgentValue === "string" && selectedAgentValue) {
            agentDGName = selectedAgentValue;
            agentName = getAgentForDeploymentGroup(selectedAgentValue);
        }
        this.set("agentName", agentName);
        this.set("agentDGName", agentDGName);
        return this.validateConnection(
            type,
            username,
            password,
            wizardURL,
            agentName,
            isCDC,
            checkPrimaryDatabase,
            useConnectionProfile,
            connectionProfileName
        );
    },

    async validateConnection(
        type,
        username,
        password,
        wizardURL,
        agentName,
        isCDC,
        checkPrimaryDatabase = false,
        useConnectionProfile,
        connectionProfileName
    ) {
        const providerType = this.get("type");
        const config = getProviderConfig(providerType);

        // Provider-specific override (e.g. Salesforce, OJet)
        if (config?.validateConnection) {
            const adapterName_version = this.get("sourceAdapter") || null;
            const metaObjectFQN = this.get("objectID") || null;
            return config.validateConnection(this, agentName, {
                isCDC,
                checkPrimaryDatabase,
                metaObjectFQN,
                adapterName_version
            });
        }

        // Generic path: build params, call WizardHelper_ValidateConnection
        const wizardType = resolveWizardType(config, providerType);
        let params;
        const sslConfigString = this.get("SSLConfig");

        if (this.isAppAdapter() && config?.getConnectionParams) {
            // App adapters have custom param shapes
            params = await config.getConnectionParams(providerType, this);
        } else if (config?.getConnectionParams) {
            // DB providers — use basic params and merge SSL as needed
            params = {
                UserName: username,
                Password: password,
                ConnectionURL: wizardURL,
                useConnectionProfile,
                connectionProfileName,
                ...(sslConfigString && { SSLConfig: sslConfigString })
            };
            const isILCDC = isAutomatedWizard();
            if (isILCDC) {
                const sslParams = await getParams(type, this);
                if (sslParams?.ConnectionURL) params.ConnectionURL = sslParams.ConnectionURL;
                if (sslParams?.sslConfigMap) params.sslConfigMap = sslParams.sslConfigMap;
            } else if (type === DB_PROVIDERS.MYSQL?.id?.toLowerCase() && !isCDC) {
                const sslParams = await getParams(type, this);
                if (sslParams?.ConnectionURL) params.ConnectionURL = sslParams.ConnectionURL;
            } else if (
                type === DB_PROVIDERS.SQLSERVER?.id?.toLowerCase() ||
                type === DB_PROVIDERS.MSJET?.id?.toLowerCase()
            ) {
                const sslParams = await getParams(type, this);
                if (sslParams?.ConnectionURL) params.ConnectionURL = sslParams.ConnectionURL;
                if (sslParams?.sslConfigMap) params.sslConfigMap = sslParams.sslConfigMap;
            }
        } else {
            params = {
                UserName: username,
                Password: password,
                ConnectionURL: wizardURL,
                useConnectionProfile,
                connectionProfileName,
                ...(sslConfigString && { SSLConfig: sslConfigString })
            };
        }

        const adapterName_version = this.get("sourceAdapter") || null;
        const metaObjectFQN = this.get("objectID") || null;
        if (this.get("encrypted") && params.Password && typeof params.Password !== "object") {
            params.Password = { encrypted: params.Password };
        }
        const data = await api
            .WizardHelper_ValidateConnection(wizardType, params, agentName, metaObjectFQN, adapterName_version)
            .catch(e => {
                console.error(e);
                throw "Problem Validating Connection. Please check the logs for detailed information.";
            });
        if (data.result === true) {
            if (data.message) this.set("authToken", data.message);
            return data;
        }
        throw data;
    },

    async checkCDCPermissions() {
        const type = this.get("type");
        const agentName = this.get("agentName");
        const config = getProviderConfig(type);

        // Provider-specific override (e.g. MongoDB)
        if (config?.checkCDCPermissions) {
            return config.checkCDCPermissions(this, agentName);
        }

        const metaObjectFQN = this.get("objectID") || null;
        const adapterName_version = this.get("sourceAdapter") || null;

        // Salesforce CDC uses ValidateConnection instead of CheckCDCConfigurations
        if (type === DB_PROVIDERS.SALESFORCE.id) {
            const params = await getParams(type, this);
            if (this.get("encrypted") && params.Password && typeof params.Password !== "object") {
                params.Password = { encrypted: params.Password };
            }
            return api.WizardHelper_ValidateConnection(
                "SALESFORCECDC",
                params,
                agentName,
                metaObjectFQN,
                adapterName_version
            );
        }

        // Generic path
        const wizardType = resolveWizardType(config, type);
        const isDownstream = type === DB_PROVIDERS.OJET?.id ? this.isDownstreamInfoFilled() : false;
        const params = await getParams(type, this, isDownstream);
        if (this.get("encrypted") && params.Password && typeof params.Password !== "object") {
            params.Password = { encrypted: params.Password };
        }
        return api.WizardHelper_CheckCDCConfigurations(
            wizardType,
            params,
            agentName,
            metaObjectFQN,
            adapterName_version
        );
    },

    async fetchTableWrapper(isCDC) {
        const type = this.get("type");
        const agentName = this.get("agentName");
        const config = getProviderConfig(type);
        const metaObjectFQN = this.get("objectID") || null;
        const adapterName_version = this.get("sourceAdapter") || null;

        // Provider-specific override (return undefined to fall through)
        if (config?.fetchTableWrapper) {
            const result = await config.fetchTableWrapper(this, agentName, metaObjectFQN, adapterName_version, {
                isCDC
            });
            if (result !== undefined) return result;
        }

        // Generic path: tableWrapper drives which API to call
        const provider = DB_PROVIDERS[type];
        const wizardType = resolveWizardType(config, type);
        const params = config?.getConnectionParams
            ? await config.getConnectionParams(type, this)
            : await getParams(type, this);

        const apiFn = TABLE_WRAPPER_API[provider.tableWrapper];
        if (!apiFn) {
            throw new Error(
                'No table wrapper fetch API for tableWrapper="' + provider.tableWrapper + '" (type=' + type + ")"
            );
        }
        return apiFn(wizardType, params, agentName, metaObjectFQN, adapterName_version);
    },

    fetchObjects(channel, isCDC) {
        return api.WizardHelper_GetObjectsWithExtraParam(
            "SALESFORCECDC",
            {
                useConnectionProfile: this.get("useConnectionProfile"),
                connectionProfileName: this.get("connectionProfileName"),
                Username: this.get("username"),
                Password: this.get("password"),
                securityToken: this.get("securityToken"),
                consumerKey: this.get("consumerKey"),
                consumerSecret: this.get("consumerSecret"),
                accesstoken: this.get("authToken"),
                apiEndPoint: this.get("host")
            },
            channel,
            this.get("agentName")
        );
    }
});

export const Table = Backbone.Model.extend({
    defaults: {
        id: null,
        name: null,
        isValid: true,
        isSelected: false,
        fullTableName: null
    }
});

export const Tables = Backbone.Collection.extend({
    model: Table
});

export const Schema = Backbone.Model.extend({
    defaults: {
        id: null,
        tables: null,
        isEnabled: false,
        schemaName: null,
        schemaDisplayName: null
    }
});

export const Schemas = Backbone.Collection.extend({
    model: Schema
});

export const WizardModel = Backbone.Model.extend({
    defaults: {
        appName: null,
        connectionParams: null,
        schemas: null,
        template: null,
        isCDC: false
    },
    getTargetType() {
        let target = this.get("template").steps ? this.get("template").steps[1] : null;
        switch (target) {
            case "SnowflakeWriter":
                return "snowflake";
            case "OutputPostgresDatabase":
            case "CloudSqlPostgreSqlWriter":
            case "AzurePostgreSqlWriter":
            case "CrunchyDataWriter":
            case "NeonWriter":
            case "LakebaseWriter":
                return DB_PROVIDERS.POSTGRES.name.toLowerCase();
            case "OutputOracleDatabase":
                return DB_PROVIDERS.ORACLE.name.toLowerCase();
            case "OutputMySQLDatabase":
            case "CloudSqlMySqlWriter":
                return DB_PROVIDERS.MYSQL.name.toLowerCase();
            case "OutputSQLServerDatabase":
            case "CloudSqlSQLServerWriter":
            case "OutputAzureSQLServerDatabase":
                return DB_PROVIDERS.SQLSERVER.name.toLowerCase();
            case "MariaDBDatabase":
                return DB_PROVIDERS.MARIADB.name.toLowerCase();
            case "BigQuery":
                return "bigquery";
            case "AzureSQLDWHWriter":
            case "FabricDataWarehouseWriter":
                return "azuresynapse";
            case "DeltaLakeWriter":
                return "deltalake";
            default:
                throw new Error("Invalid target type set during Schema Migration Compatibility Check");
        }
    }
});
